import { getPageActionContext } from './context/pageAttributes';
import { getNewRelic } from './getNewRelic';

export type PageActionAttributes = Record<string, string | number | boolean>;

// addPageAction drops calls until the SPA agent's generic-events aggregate loads, which is after window `load`.
// Retry until featAggregate exists. That field is internal to the vendored 1.293.0 agent, so send anyway after 15s.
const PAGE_ACTION_READY_WAIT_MS = 15000;
const PAGE_ACTION_RETRY_INTERVAL_MS = 100;

const GENERIC_EVENTS_FEATURE = 'generic_events';

type BrowserAgent = {
  features?: Record<string, { featAggregate?: unknown } | undefined>;
};

function isPageActionAggregateReady() {
  const nreum = (
    window as Window & {
      NREUM?: { initializedAgents?: Record<string, BrowserAgent> };
    }
  ).NREUM;
  const agents = nreum?.initializedAgents;
  if (!agents) return false;

  return Object.values(agents).some(
    (agent) => agent?.features?.[GENERIC_EVENTS_FEATURE]?.featAggregate != null
  );
}

function sendPageAction(actionName: string, attributes: PageActionAttributes) {
  getNewRelic()?.addPageAction(actionName, attributes);
}

export function reportPageAction(actionName: string, attributes?: PageActionAttributes) {
  if (process.env.NEXT_PUBLIC_NEW_RELIC_DISABLED === 'true' || typeof window === 'undefined') {
    return;
  }

  const payload: PageActionAttributes = {
    ...getPageActionContext(),
    ...attributes,
  };

  if (isPageActionAggregateReady()) {
    sendPageAction(actionName, payload);
    return;
  }

  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (!isPageActionAggregateReady() && Date.now() - startedAt < PAGE_ACTION_READY_WAIT_MS) {
      return;
    }

    clearInterval(timer);
    sendPageAction(actionName, payload);
  }, PAGE_ACTION_RETRY_INTERVAL_MS);
}
